import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getRoleTheme } from "@/lib/roleTheme";
import UserChip from "@/components/UserChip";
import RoleBadge from "@/components/RoleBadge";
import {
  LayoutDashboard, Home as HomeIcon, FileText, MessageSquare, Calendar,
  Users, Building2, Briefcase, Settings, SlidersHorizontal, LogOut, Search, ShieldAlert, AlertOctagon, Activity,
  Menu, X,
} from "lucide-react";

const linksByRole = {
  seller: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/submit-property", label: "Submit Property", icon: HomeIcon },
    { to: "/messages", label: "Messages", icon: MessageSquare },
    { to: "/appointments", label: "Appointments", icon: Calendar },
    { to: "/profile", label: "Profile", icon: Settings },
    { to: "/settings", label: "Settings", icon: SlidersHorizontal },
  ],
  investor: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/opportunities", label: "Opportunities", icon: Search },
    { to: "/my-offers", label: "My Offers", icon: FileText },
    { to: "/messages", label: "Messages", icon: MessageSquare },
    { to: "/appointments", label: "Appointments", icon: Calendar },
    { to: "/profile", label: "Profile", icon: Settings },
    { to: "/settings", label: "Settings", icon: SlidersHorizontal },
  ],
  realtor: "investor",
  attorney: "investor",
  lender: "investor",
  contractor: "investor",
  title: "investor",
  admin: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/providers", label: "Pending", icon: ShieldAlert },
    { to: "/admin/properties", label: "Properties", icon: Building2 },
    { to: "/admin/offers", label: "Offers", icon: Briefcase },
    { to: "/admin/conversations", label: "Conversations", icon: MessageSquare },
    { to: "/admin/appointments", label: "Appointments", icon: Calendar },
    { to: "/admin/danger", label: "Danger Zone", icon: AlertOctagon },
    { to: "/admin/audit", label: "Activity Log", icon: Activity },
    { to: "/profile", label: "Profile", icon: Settings },
    { to: "/settings", label: "Settings", icon: SlidersHorizontal },
  ],
};

function resolveLinks(role) {
  const l = linksByRole[role];
  if (typeof l === "string") return linksByRole[l];
  return l || linksByRole.seller;
}

function SidebarBody({ t, RoleIcon, links, logout, onItemClick }) {
  return (
    <>
      <div className="h-1.5" style={{ backgroundColor: t.accent }} />
      <div className="px-5 py-5 flex items-center gap-3 border-b border-white/10">
        <div
          className="h-9 w-9 rounded-sm flex items-center justify-center font-bold font-display text-[var(--adn-navy)]"
          style={{ backgroundColor: t.accent }}
          data-testid="sb-role-icon"
        >
          <RoleIcon size={18} />
        </div>
        <div className="leading-tight">
          <div className="font-display font-semibold text-[15px]">ADN Marketplace</div>
          <div className="text-[10px] tracking-[0.18em] uppercase mt-0.5 font-bold" style={{ color: t.accent }}>
            {t.tagline}
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 dark-scroll overflow-y-auto">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/dashboard"}
            onClick={onItemClick}
            data-testid={`sb-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            style={({ isActive }) => isActive ? { color: t.accent, background: `${t.accent}14`, borderLeft: `2px solid ${t.accent}` } : {}}
            className={({ isActive }) => `sb-link ${isActive ? "active-role" : ""}`}
          >
            <l.icon size={16} />
            <span>{l.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-white/10">
        <Link to="/" onClick={onItemClick} className="text-[11px] text-white/55 hover:text-white tracking-wide uppercase">← Back to site</Link>
        <button
          data-testid="sb-logout"
          onClick={() => { onItemClick?.(); logout(); }}
          className="mt-3 w-full sb-link justify-start"
        >
          <LogOut size={16} /> <span>Sign out</span>
        </button>
      </div>
    </>
  );
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const links = resolveLinks(user?.role);
  const t = getRoleTheme(user?.role);
  const RoleIcon = t.icon;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Auto-close drawer on route change
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-[var(--adn-cream)]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col bg-[var(--adn-navy)] text-white grain">
        <SidebarBody t={t} RoleIcon={RoleIcon} links={links} logout={logout} />
      </aside>

      {/* Mobile drawer + overlay */}
      {mobileNavOpen && (
        <>
          <button
            aria-label="Close menu"
            onClick={() => setMobileNavOpen(false)}
            data-testid="sb-mobile-overlay"
            className="md:hidden fixed inset-0 bg-black/55 z-40"
          />
          <aside
            className="md:hidden fixed top-0 left-0 bottom-0 w-[78vw] max-w-[300px] z-50 bg-[var(--adn-navy)] text-white grain flex flex-col shadow-2xl"
            style={{ paddingTop: "env(safe-area-inset-top)" }}
            data-testid="sb-mobile-drawer"
          >
            <button
              aria-label="Close menu"
              onClick={() => setMobileNavOpen(false)}
              data-testid="sb-mobile-close"
              className="absolute top-3 right-3 text-white/70 hover:text-white p-2 z-10"
              style={{ marginTop: "env(safe-area-inset-top)" }}
            >
              <X size={20} />
            </button>
            <SidebarBody t={t} RoleIcon={RoleIcon} links={links} logout={logout} onItemClick={() => setMobileNavOpen(false)} />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0">
        {/* Top bar: signed-in indicator always visible */}
        <div
          className="bg-white border-b border-[var(--adn-line)] px-5 md:px-10 py-3 flex items-center justify-between gap-3 sticky top-0 z-30"
          style={{ paddingTop: "max(0.75rem, calc(env(safe-area-inset-top) + 0.5rem))" }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              aria-label="Open menu"
              data-testid="sb-mobile-toggle"
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden p-2 -ml-2 text-[var(--adn-navy)]"
            >
              <Menu size={22} />
            </button>
            <div className="md:hidden flex items-center gap-2 min-w-0">
              <span
                className="h-7 w-7 rounded-sm flex items-center justify-center shrink-0"
                style={{ backgroundColor: t.accentSoft, color: t.accentText }}
              >
                <RoleIcon size={14} />
              </span>
              <Link to="/" className="font-display font-semibold text-[var(--adn-navy)] truncate">ADN</Link>
            </div>
            <div className="hidden md:flex items-center gap-3 min-w-0">
              <span
                className="h-7 w-7 rounded-sm flex items-center justify-center shrink-0"
                style={{ backgroundColor: t.accentSoft, color: t.accentText }}
              >
                <RoleIcon size={14} />
              </span>
              <div className="leading-tight min-w-0">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--adn-text-muted)] font-bold">Signed in as</div>
                <div className="text-sm font-medium truncate max-w-[260px]">{user?.name || user?.email}</div>
              </div>
              <span data-testid="signed-in-indicator" className="hidden md:inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--adn-emerald)]">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--adn-emerald)] animate-pulse" /> Live session
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <RoleBadge size="lg" />
            <UserChip />
          </div>
        </div>

        {/* Role-colored content header strip (subtle) */}
        <div className="h-1" style={{ backgroundColor: t.accent, opacity: 0.5 }} />

        <div className="p-6 md:p-10">{children}</div>
      </main>
    </div>
  );
}
